import type { NetworkInfo } from "@alch/dx-entities";
import urlJoin from "url-join";

enum Url {
  BillingSupport = "https://www.alchemy.com/support/categories/billing?page=1",
  CustomRpcGuide = "https://www.alchemy.com/blog/supercharge-metamask",
  Support = "mailto:support@alchemy.com",
  GetAmplified = "https://alchemyapi.typeform.com/to/Yhl13CyU",
  Spearmint = "https://spearmint.xyz/",
  University = "https://alchemy.com/university",
  UniversitySolidityCourse = "https://www.alchemy.com/university/courses/solidity",
  YouTubeChannel = "https://www.youtube.com/c/alchemyplatform",
  AlchemyPricing = "https://www.alchemy.com/pricing",
  AccountKit = "https://alchemy.com/docs/wallets/",
  EveryoneOnchainFund = "https://www.alchemy.com/everyone-onchain-fund?utm_source=account_kit&utm_medium=dashboard&utm_campaign=everyone_onchain_fund",
  Home = "https://alchemy.com",
  ContactSales = "https://www.alchemy.com/contact-sales",
  ContactSalesRollups = "https://www.alchemy.com/contact-sales-rollups",
  ContactSalesDedicated = "https://www.alchemy.com/contact-sales-dedicated-clusters",
  Terms = "https://www.alchemy.com/policies/terms",
  AccountsSupplementTerms = "https://www.alchemy.com/terms-conditions/supplemental-terms",
  AccountsUserTerms = "https://www.alchemy.com/terms-conditions/end-user-terms",
  EmbeddedAccounts = "https://alchemy.com/docs/wallets",
  RaasSignup = "https://alchemyapi.typeform.com/to/l0LWIgov",
  RaasContactSales = "https://www.alchemy.com/contact-sales-rollups",
  Faucets = "https://www.alchemy.com/faucets",
  StatusPage = "https://status.alchemy.com",
  DedicatedInfra = "https://www.alchemy.com/dedicated-clusters",
}

enum AlchemyDocsUrl {
  AccountAbstractionSDK = "https://alchemy.com/docs/wallets/",
  Alerts = "https://docs.alchemy.com/docs/dashboard-alerts",
  ArbitrumNovaQuickStart = "https://docs.alchemy.com/reference/arbitrum-nova-api-quickstart",
  ArchiveData = "https://docs.alchemy.com/docs/what-is-archive-data-on-ethereum",
  AvalancheQuickStart = "https://docs.alchemy.com/reference/avalanche-api-quickstart",
  BaseApi = "https://docs.alchemy.com/reference/base-api-quickstart",
  BlockTimestampApi = "https://docs.alchemy.com/reference/blocks-by-timestamp",
  BerachainQuickStart = "https://docs.alchemy.com/reference/berachain-api-quickstart",
  BNBSmartChainQuickStart = "https://docs.alchemy.com/reference/bnb-smart-chain-api-quickstart",
  BundlerApi = "https://docs.alchemy.com/reference/bundler-api-quickstart",
  ChainApi = "https://docs.alchemy.com/reference/chain-apis-overview",
  ComputeUnits = "https://docs.alchemy.com/reference/compute-units",
  CreateWeb3Dapp = "https://docs.alchemy.com/docs/create-web3-dapp-quickstart",
  CrossFiQuickStart = "https://docs.alchemy.com/reference/crossfi-api-quickstart",
  CUCosts = "https://docs.alchemy.com/reference/compute-unit-costs",
  CustomWebhooksQuickstart = "https://docs.alchemy.com/reference/custom-webhooks-quickstart",
  DebugApi = "https://docs.alchemy.com/reference/debug-api-quickstart",
  ErrorReference = "https://docs.alchemy.com/reference/error-reference",
  ErrorCodes = `${AlchemyDocsUrl.ErrorReference}#standard-json-rpc-errors`,
  EthCall = "https://docs.alchemy.com/reference/eth-call",
  EthereumApiQuickstart = "https://docs.alchemy.com/reference/ethereum-api-quickstart",
  FantomQuickStart = "https://docs.alchemy.com/reference/fantom-opera-api-quickstart",
  FeatureSupport = "https://docs.alchemy.com/reference/feature-support-by-chain",
  FlowQuickStart = "https://docs.alchemy.com/reference/flow-api-quickstart",
  GasManager = "https://alchemy.com/docs/gas-manager-services",
  GasManagerAdminApi = "https://alchemy.com/docs/reference/gas-manager-coverage-api-quickstart",
  GasManagerCoverageApi = "https://alchemy.com/docs/reference/gas-manager-coverage-api-quickstart",
  GasOptimizedTransactions = "https://docs.alchemy.com/reference/gas-optimized-transactions",
  GasSponsorship = "https://www.alchemy.com/docs/wallets/react/sponsor-gas",
  GnosisQuickStart = "https://docs.alchemy.com/reference/gnosis-api-quickstart",
  Grpc = "https://docs.alchemy.com/reference/yellowstone-grpc-overview",
  Home = "https://docs.alchemy.com/",
  LineaQuickStart = "https://docs.alchemy.com/reference/linea-chain-api-quickstart",
  LogEvents = "https://docs.alchemy.com/docs/how-to-get-on-chain-events",
  MantleQuickStart = "https://docs.alchemy.com/reference/mantle-chain-api-quickstart",
  MetisQuickStart = "https://docs.alchemy.com/reference/metis-chain-api-quickstart",
  MultiChainApps = "https://docs.alchemy.com/docs/multichain-apps",
  NftApi = "https://docs.alchemy.com/reference/nft-api-quickstart",
  OpBNBQuickStart = "https://docs.alchemy.com/reference/opbnb-chain-api-quickstart",
  PolygonZkEVMQuickstart = "https://docs.alchemy.com/reference/polygon-zkevm-api-quickstart",
  PortfolioApis = "https://docs.alchemy.com/reference/portfolio-apis",
  PricesApi = "https://docs.alchemy.com/reference/prices-api-quickstart",
  PricingAutoscale = "https://docs.alchemy.com/reference/pricing-plans#auto-scale-compute-growth--scale-tier-only",
  RaasDocs = "https://docs.alchemy.com/reference/rollups-quickstart",
  ReinforcedTransactions = "https://docs.alchemy.com/reference/reinforced-transactions",
  ScrollQuickStart = "https://docs.alchemy.com/reference/scroll-api-quickstart",
  SmartWebSockets = "https://docs.alchemy.com/reference/sdk-websockets-endpoints",
  SubscriptionApi = "https://docs.alchemy.com/reference/subscription-api",
  TokenApi = "https://docs.alchemy.com/reference/token-api-quickstart",
  TraceApi = "https://docs.alchemy.com/reference/trace-api-quickstart",
  TransactApi = "https://docs.alchemy.com/reference/transact-api-quickstart",
  TransactionReceiptsApi = "https://docs.alchemy.com/reference/alchemy-gettransactionreceipts",
  TransactionSimulationApi = "https://docs.alchemy.com/reference/simulation",
  TransfersApi = "https://docs.alchemy.com/reference/transfers-api-quickstart",
  UtxoAddons = "https://www.alchemy.com/docs/bitcoin/utxo",
  UserOperationsApi = "https://alchemy.com/docs/node/bundler-api/useroperation-simulation-endpoints/alchemy-simulate-user-operation-asset-changes",
  WebhooksApi = "https://docs.alchemy.com/reference/notify-api-quickstart",
  ZetaChainQuickStart = "https://docs.alchemy.com/reference/zetachain-api-quickstart ",
  ZKsyncQuickStart = "https://docs.alchemy.com/reference/zksync-api-quickstart",
  SoneiumQuickStart = "https://docs.alchemy.com/reference/soneium-api-quickstart",
  WalletApiLogs = "https://docs.alchemy.com/wallets/transactions/debug-transactions",
  Wallets = "https://docs.alchemy.com/wallets",
  WorldChainQuickStart = "https://docs.alchemy.com/reference/worldchain-api-quickstart",
}

// Removes any urls from sentence, helpful for printing errors and not exposing internal urls externally
// Yes - its chatgpt generated
function stripUrls(sentence: string): string {
  const words = sentence.split(/\s+/); // Split the sentence into words
  function isUrl(word: string): boolean {
    // This regex pattern checks for common URL structures
    const urlRegex = /^(?:(?:https?|ftp):\/\/)?[\w/:%#$&?()~.=+-]+$/i;
    return urlRegex.test(word);
  }
  // Filter out words that look like URLs
  const filteredWords = words.filter((word) => !isUrl(word));
  // Join the filtered words back into a sentence
  return filteredWords.join(" ");
}

const buildExplorerUrl = (
  networkInfo: Pick<NetworkInfo, "explorerUrl">,
  path: "tx" | "address" | "block",
  hash: string,
) => {
  const [baseUrl, qs] = networkInfo.explorerUrl.split("?");

  if (!baseUrl) {
    return "";
  }

  return urlJoin(baseUrl, path, hash, qs ? `?${qs}` : "");
};

/**
 * Copies over the given params from the current URL to the target URL path, if they exist.
 * @param currentUrlOrPathStr location.href
 * @param targetPathStr a path like /app/123
 * @param queryParamKeys the keys to copy over like ['view_as_user']
 */
function preserveQueryParamsInPath(
  currentUrlOrPathStr: string,
  targetPathStr: string,
  queryParamKeys: string[],
) {
  const url = new URL(targetPathStr, "http://thisisignored.com");
  const windowUrl = new URL(currentUrlOrPathStr, "http://thisisignored.com");

  const queryParams = new URLSearchParams(windowUrl.search);
  queryParams.forEach((value, key) => {
    if (queryParamKeys.includes(key)) {
      url.searchParams.set(key, value);
    }
  });

  return `${url.pathname}${url.search}${url.hash}`;
}

export {
  AlchemyDocsUrl,
  buildExplorerUrl,
  preserveQueryParamsInPath,
  stripUrls,
  Url,
};
